import { useState, useMemo, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useManifest } from '../hooks/use-manifest';
import { updateManifest, deleteManifest, restoreManifest, flagBarcode, unflagBarcode } from '../api/manifests-api';
import { useUndoHistory } from '@/contexts/undo-history-context';
import type { ManifestStatus } from '@/types/manifest';
import { ManifestStatusBadge } from '../components/manifest-status-badge';
import { ProgressBar } from '../components/progress-bar';
import { BarcodeImportSection } from '../components/barcode-import-section';
import { AddBarcodeInput } from '../components/add-barcode-input';
import { BarcodeList } from '../components/barcode-list';
import { BarcodeModal } from '../components/barcode-modal';
import { BarcodeExportButton } from '../components/barcode-export-button';
import { formatToEST } from '@/lib/utils/date-format';
import { useDialog } from '@/hooks/useDialog';
import { Dialog } from '@/components/ui/dialog';
import { useAuth } from '@/features/auth/hooks/use-auth';

export function ManifestDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const manifestId = id ? parseInt(id, 10) : null;
  const { manifest, isLoading, isRefetching, error, refetch } = useManifest(manifestId);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [viewingBarcode, setViewingBarcode] = useState<string | null>(null);
  const [isEditingName, setIsEditingName] = useState(false);
  const [isSavingName, setIsSavingName] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const { dialog, closeDialog, showConfirm, showError } = useDialog();
  const { push } = useUndoHistory();
  const { user } = useAuth();
  const isAdmin = user?.role === 'Admin';

  // Auto-refresh manifest data every 30 seconds. Pause while the name is being
  // edited so a background refetch can't shift manifest.bargeName under the
  // in-progress edit (avoids a stale unchanged-name comparison on save).
  useEffect(() => {
    if (isEditingName) return;
    const interval = setInterval(() => {
      refetch();
    }, 30000);
    return () => clearInterval(interval);
  }, [refetch, isEditingName]);


  // Detect duplicate barcodes (must be called before early returns)
  const duplicateInfo = useMemo(() => {
    if (!manifest?.barcodes) {
      return { hasDuplicates: false, duplicateCount: 0, totalDuplicateInstances: 0 };
    }
    const counts = new Map<string, number>();
    manifest.barcodes.forEach(b => counts.set(b.barcodeValue, (counts.get(b.barcodeValue) || 0) + 1));
    const duplicates = Array.from(counts.entries()).filter(([_, count]) => count > 1);
    return {
      hasDuplicates: duplicates.length > 0,
      duplicateCount: duplicates.length,
      totalDuplicateInstances: duplicates.reduce((sum, [_, count]) => sum + count, 0)
    };
  }, [manifest?.barcodes]);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="h-8 bg-gray-700 rounded w-48 animate-pulse" />
        <div className="h-64 bg-gray-800 rounded animate-pulse" />
      </div>
    );
  }

  // isRefetching is used for a non-intrusive indicator — no skeleton shown

  if (error || !manifest) {
    return (
      <div className="space-y-6">
        <Link to="/manifests" className="text-blue-500 hover:text-blue-400">
          &larr; Back to Manifests
        </Link>
        <div className="bg-red-500/10 border border-red-500 text-red-500 px-4 py-3 rounded">
          {error || 'Manifest not found'}
        </div>
      </div>
    );
  }

  const scannedCount = manifest.barcodes.filter((b) => b.isScanned).length;
  const expectedCount = manifest.barcodes.length;
  const isOpen = manifest.status === 'Open';


  const handleFlagToggle = async (barcodeId: number, currentlyFlagged: boolean) => {
    try {
      if (currentlyFlagged) {
        await unflagBarcode(manifest.id, barcodeId);
      } else {
        await flagBarcode(manifest.id, barcodeId);
      }
      refetch();
    } catch {
      showError('Error', 'Failed to update flag status');
    }
  };

  const handleStatusChange = async (newStatus: ManifestStatus) => {
    setIsUpdating(true);
    try {
      await updateManifest(manifest.id, { status: newStatus });
      refetch();
    } finally {
      setIsUpdating(false);
    }
  };

  const startEditingName = () => {
    setNameDraft(manifest.bargeName);
    setIsEditingName(true);
  };

  const cancelEditingName = () => {
    setIsEditingName(false);
  };

  const handleNameSave = async () => {
    const trimmed = nameDraft.trim();
    // No-op when empty/whitespace-only or unchanged — just exit edit mode.
    if (!trimmed || trimmed === manifest.bargeName) {
      setIsEditingName(false);
      return;
    }
    setIsSavingName(true);
    try {
      await updateManifest(manifest.id, { bargeName: trimmed });
      // Await the refetch so the heading reflects the new name before the
      // editor closes (avoids briefly re-showing the old name).
      await refetch();
      setIsEditingName(false);
    } catch {
      // Keep edit mode open so the user can retry.
      showError('Update Failed', 'Failed to update the barge name');
    } finally {
      setIsSavingName(false);
    }
  };

  const handleDelete = async () => {
    showConfirm(
      'Delete Manifest',
      'Delete this manifest? You can undo this from the history menu.',
      async () => {
        setIsDeleting(true);
        try {
          const response = await deleteManifest(manifest.id);
          if (response.success) {
            push(
              manifest.bargeName,
              () => restoreManifest(manifest.id).then(() => {}),
              'Manifest deleted',
              () => navigate(`/manifests/${manifest.id}`)
            );
            navigate('/manifests');
          } else {
            showError('Delete Failed', response.message || 'Failed to delete manifest');
          }
        } catch {
          showError('Delete Failed', 'An error occurred while deleting the manifest');
        } finally {
          setIsDeleting(false);
        }
      },
      'Delete'
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Link to="/manifests" className="text-blue-500 hover:text-blue-400">
          &larr; Back
        </Link>
        {isRefetching && (
          <span className="text-xs text-gray-500 animate-pulse">Refreshing...</span>
        )}
      </div>

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          {isEditingName ? (
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={nameDraft}
                onChange={(e) => setNameDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleNameSave();
                  } else if (e.key === 'Escape') {
                    e.preventDefault();
                    cancelEditingName();
                  }
                }}
                maxLength={200}
                autoFocus
                disabled={isSavingName}
                aria-label="Barge name"
                className="text-3xl font-bold px-3 py-1 rounded bg-gray-700 border border-gray-600 focus:border-blue-500 focus:outline-none disabled:opacity-60"
              />
              <button
                type="button"
                onClick={handleNameSave}
                disabled={isSavingName}
                aria-label="Save barge name"
                className="px-3 py-2 text-sm bg-blue-600 hover:bg-blue-700 disabled:bg-blue-800 disabled:cursor-not-allowed rounded transition-colors"
              >
                {isSavingName ? 'Saving...' : 'Save'}
              </button>
              <button
                type="button"
                onClick={cancelEditingName}
                disabled={isSavingName}
                aria-label="Cancel editing barge name"
                className="px-3 py-2 text-sm bg-gray-700 hover:bg-gray-600 disabled:cursor-not-allowed rounded transition-colors"
              >
                Cancel
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <h1 className="text-3xl font-bold">{manifest.bargeName}</h1>
              <button
                type="button"
                onClick={startEditingName}
                aria-label="Edit barge name"
                className="p-1.5 text-gray-400 hover:text-gray-200 hover:bg-gray-700 rounded transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </button>
            </div>
          )}
          <p className="text-gray-400">
            Created {formatToEST(manifest.createdAt, false)}
            {manifest.completedAt && (
              <> &middot; Completed {formatToEST(manifest.completedAt, false)}</>
            )}
          </p>
        </div>

        <div className="flex items-center gap-4">
          <ManifestStatusBadge status={manifest.status} />

          <select
            value={manifest.status}
            onChange={(e) => handleStatusChange(e.target.value as ManifestStatus)}
            disabled={isUpdating}
            className="px-3 py-2 rounded bg-gray-700 border border-gray-600 focus:border-blue-500 focus:outline-none"
          >
            <option value="Flagged">Flagged</option>
            <option value="Open">Open</option>
            <option value="InProgress">In Progress</option>
            <option value="Complete">Complete</option>
          </select>

          <Link
            to={`/manifests/${manifest.id}/report`}
            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded transition-colors"
          >
            View Report
          </Link>

          {expectedCount > 0 && (
            <BarcodeExportButton
              barcodes={manifest.barcodes.map((b) => ({ value: b.barcodeValue }))}
              bargeName={manifest.bargeName}
            />
          )}

          {isOpen && (
            <button
              onClick={handleDelete}
              disabled={isDeleting}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-red-800 rounded transition-colors"
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </button>
          )}
        </div>
      </div>

      <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
        <ProgressBar current={scannedCount} total={expectedCount} />
      </div>

      {duplicateInfo.hasDuplicates && (
        <div className="bg-yellow-900/20 border border-yellow-500 text-yellow-400 px-4 py-3 rounded">
          <div className="flex items-start gap-2">
            <svg className="w-5 h-5 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <div>
              <p className="font-semibold">Duplicate Barcodes Detected</p>
              <p className="text-sm mt-1">
                Found {duplicateInfo.duplicateCount} barcode value{duplicateInfo.duplicateCount !== 1 ? 's' : ''} with multiple entries ({duplicateInfo.totalDuplicateInstances} total instances).
                Use the "Duplicates" filter below to view and remove duplicate entries.
              </p>
            </div>
          </div>
        </div>
      )}

      {isOpen && (
        <>
          <BarcodeImportSection manifestId={manifest.id} onImported={refetch} />

          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
            <AddBarcodeInput manifestId={manifest.id} onAdded={refetch} />
          </div>
        </>
      )}

<div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
        <h3 className="text-lg font-semibold mb-4">Barcodes ({expectedCount})</h3>
        <BarcodeList
          manifestId={manifest.id}
          manifestName={manifest.bargeName}
          barcodes={manifest.barcodes}
          onDeleted={refetch}
          canDelete={true}
          onViewBarcode={setViewingBarcode}
          onFlagToggle={handleFlagToggle}
          canSetStatus={isAdmin}
        />
      </div>


      <BarcodeModal
        value={viewingBarcode}
        onClose={() => setViewingBarcode(null)}
      />

      <Dialog
        isOpen={dialog.isOpen}
        onClose={closeDialog}
        title={dialog.title}
        message={dialog.message}
        type={dialog.type}
        confirmText={dialog.confirmText}
        cancelText={dialog.cancelText}
        onConfirm={dialog.onConfirm}
        showCancel={dialog.showCancel}
      />
    </div>
  );
}
